import moment, { Moment } from 'moment'
import React, { useCallback, useState, useContext } from 'react'

import DateRangePicker from 'tripPlanner/Tripkit/Common/DateRangePicker'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { ModalElementContext } from 'contexts/ModalProvider'
import { Locations } from 'tripPlanner/pages/trip/GenerateTripPage'

interface Props {
  initStartDate?: Moment
  initEndDate?: Moment
  onSetDates: (startDate?: Moment, endDate?: Moment) => void
  locations?: Array<Locations>
}

function AITripDatePicker(props: Props) {
  const {
    initStartDate,
    initEndDate,
    onSetDates,
    locations,
  } = props

  const modalElement = useContext(ModalElementContext)
  const closeModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(
    initStartDate,
  )
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(initEndDate)

  const onDatesChange = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => {
      setStartDate(dates.startDate)
      setEndDate(dates.endDate)

      if (dates.startDate && dates.endDate && locations?.length === 1) {
        onSetDates(dates.startDate, dates.endDate)
      }
    },
    [setStartDate, setEndDate, onSetDates, locations],
  )

  const onConfirm = useCallback(() => {
    if (startDate && endDate) {
      onSetDates(startDate, endDate)
    }
    closeModal()
  }, [startDate, endDate, onSetDates, closeModal])

  const onCancel = useCallback(() => {
    closeModal()
  }, [closeModal])

  if (locations?.length === 1) {
    return (
      <DateRangePicker
        onDatesChange={onDatesChange}
        startDate={startDate}
        endDate={endDate}
        allowSameDay={false}
        showTripDates
      />
    )
  }

  return (
    <ModalBase size="S">
      <ModalHeader title="Select Trip Dates" onCloseButtonClick={onCancel} />
      <ModalBody>
        <ModalContent>
          <DateRangePicker
            onDatesChange={onDatesChange}
            startDate={startDate}
            endDate={endDate}
            allowSameDay={false}
            showTripDates
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Confirm',
          disabled: !startDate || !endDate,
          onClick: onConfirm,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          onClick: onCancel,
        }}
      />
    </ModalBase>
  )
}

export default AITripDatePicker
